<script>
    import { slide, blur } from 'svelte/transition';
    import tippy from "sveltejs-tippy";
    import WidgetBox from './WidgetBox.svelte';
    import { pin } from './pincelate-store.js';
    import { tick } from 'svelte';
    import { manipulate, parse } from './pincelate-funcs.js';
    import TemperatureSlider from './util/TemperatureSlider.svelte';
    import ExampleButton from './util/ExampleButton.svelte';
    import WordCount from './util/WordCount.svelte';

    let frame = async() => new Promise(resolve => { requestAnimationFrame(resolve); });

    let widgetbox;

    function calcLetterMap(st) {
        let out = {};
        for (const [key, val] of Object.entries(st)) {
            if (!val) {
                out[key] = 10000;
            }
        }
        return out;
    }

    async function manipulateWrap() {
        history.push(currentText.slice());
        let lastDitch = Object.keys(letterState).filter((k) => !letterState[k]).join("") + "\\^";
        let lastDitchRe = new RegExp("[" + lastDitch + "]", "ig");
        decodingInProgress = true;
        widgetbox.startDecode();
        await tick();
        let iter = manipulate(
            $pin,
            currentText,
            {},
            calcLetterMap(letterState),
            temperature
        );
        setTimeout(async function() {
            for await (let ct of iter) {
                currentText = ct.replaceAll(lastDitchRe, "");
                await frame();
            }
            decodingInProgress = false;
            widgetbox.endDecode();
            await tick();
        }, 100);
    }

    let currentText = 'How doth the little crocodile improve his shining tail...';
    let history = [currentText];

    let letters = "abcdefghijklmnopqrstuvwxyz";
    let letterState = {};

    function resetLetterState() {
        for (let i = 0; i < letters.length; i++) {
            letterState[letters.charAt(i)] = true;
        }
    }

    function clearLetterState() {
        for (let i = 0; i < letters.length; i++) {
            letterState[letters.charAt(i)] = false;
        }
    }

    resetLetterState();

    for (let ch of ['a', 'e', 'i', 'o', 'u']) {
        letterState[ch] = false;
    }

    let temperature = 0.25;

    let decodingInProgress = false;

    let trickData = [
        "What happens when the computer gets stuck? Try turning off all but one or two letters.",
        "Read the respelled text out loud. How close does it sound to the original text?",
        "Hitting 'Revert' brings back your text from before the most recent respelling.",
        "Try mashing your keyboard in the input field and respelling a bunch of times.",
        "Fed up with boring respellings? Turn up the 'weird' slider."
    ];

    let wordCount;
    $: wordCount = parse(currentText)[1].length;

</script>

<style>
    h4.toggle-header {
        margin-bottom: 0;
        text-transform: uppercase;
        margin-left: 1.5rem;
        margin-top: 0;
        font-size: 1rem;
        font-weight: normal;
        margin-bottom: 0.5rem;
        cursor: pointer;
    }
    textarea {
        width: 100%;
        margin: 0;
        padding: 0.75rem;
        border: 0.15rem black solid;
        height: 6em;
    }

    .letters-container {
        display: grid;   
    }

    @media screen and (max-width: 39.999rem) {
        .letters-container {
            grid-template-columns: repeat(6, 1fr);
        }
    }
    @media screen and (min-width: 40rem) {
        .letters-container {
            grid-template-columns: repeat(9, 1fr);
        }
    }

    .letter-checkbox {
        text-align: center;
        font-size: 1.5em;
        line-height: 1.5em;
    }

    .letter-checkbox .button.large {
        font-size: 1.65rem;
        line-height: 2.15rem;
    }

    .revert-button {
        background-image: url("assets/svg/_minus-medium-orange.svg");
    	background-size: 100%;
	    background-origin: border-box;
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 0.75rem;
        border: 0.15rem white solid;
    }
    .revert-button[disabled] {
        background-image: url("assets/svg/_minus-medium-grey.svg");
        cursor: default;
    }

</style>

<WidgetBox on:reset={resetLetterState} {trickData} bind:this={widgetbox}>

    <span slot="title">Respeller</span>
    <span slot="tagline">Spell the sound of a text with different letters</span>

    <div slot="widget" id="respeller">

        <div class="box orange lev1" style="position: relative; z-index: 1;" class:jittering={decodingInProgress} transition:slide|local>
            <textarea class="lev2 light light-border" spellcheck="false" placeholder="type something here..." bind:value={currentText}></textarea>
        </div>

        <div class="control-container" transition:slide|local="{{delay: 150}}">
            <div class="dark box control-tab secondary-action lev1">
                <button class="revert-button hovery"
                    id="revert"
                    disabled={history.length <= 1 || decodingInProgress}
                    title="Revert"
                    on:click={() => {currentText = history.pop()}}>&nbsp;</button>
                <label for="revert">Revert</label>
            </div>
            <WordCount value={wordCount} />
            <div class="light box control-tab primary-action lev1">
                <button class="action-button orange hovery"
                    id="apply"
                    disabled={decodingInProgress || wordCount > 64 }
                    class:processing={decodingInProgress}
                    title="Apply" on:click={manipulateWrap}>&nbsp;</button>
                <label for="apply">Apply</label>
            </div>
        </div>

        <div class="box light lev1" style="padding-left: 0; padding-right: 0;" transition:slide|local="{{delay: 350}}">
            <h4 class="toggle-header"><span use:tippy="{{content: 'The model can only use selected letters to respell the text.'}}">
                AVAILABLE LETTERS
                <div class="help">&nbsp;</span>
            </h4>
            <div class="letters-container">
                {#each letters as letter}
                <div class="letter-checkbox">
                    <div role="checkbox"
                        aria-checked="{letterState[letter]}"
                        class="lc button round large {letterState[letter] ? 'orange' : ''}"
                        on:click={() => {letterState[letter] = !letterState[letter]}}>{letter}</div>
                </div>
                {/each}
            </div>
        </div>
    </div>

    <div slot="sidetab">
        <TemperatureSlider bind:temperature={temperature} thumbURL="assets/svg/_weird-vertical-orange.svg" />
    </div>

    <div slot="modalContent">

        <p>The <b>Respeller</b> rewrites a text, trying to retain its sound while omitting certain
            letters.</p>

        <h4>How to use it</h4>
        <ul>
            <li>Tap or click on letters in the <b>Available Letters</b> section to toggle them on and off.</li>
            <li>Hit
            <ExampleButton src="assets/svg/_action-large-orange.svg" alt="Action">Apply</ExampleButton>.
            This will respell the text in the text box, omitting deactivated letters and "filling in" with
            active letters in order to get as close as possible to the sounds in the original text.</li>
            <li>Hit
                <ExampleButton src="assets/svg/_minus-medium-orange.svg" alt="Revert">Revert</ExampleButton>
                to restore the text to whatever it was before you most recently applied changes.</li>
        </ul>
            
        <h4>Tips and tricks</h4>
        <ul>
            <li>The <b>Respeller</b> works best when it has plenty of options, so
            don't turn off too many letters at a time.</li>
            <li>(Then again, it's fun to see what
            happens when the computer gets stuck, so maybe try turning off <em>all</em> the letters?)</li>
            <li>Lower values on the <b>Weird ↔︎ Normal</b> slider lead to safe, boring output. Higher values 
                push the model toward output that is less likely, but maybe more fun and expressive.</li>
            <li>Read the respelled text out loud. How close does it sound to the original text?</li>
        </ul>

    </div>

</WidgetBox>